import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { cn } from "../../utils/cn"
import { Share2, Home, Umbrella, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import GameAnalytics from "../analytics/GameAnalytics"

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        outline: "border border-input hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = "Button"


// Share Button Component
const ShareButton = ({ message, className, source }) => {
  const { t } = useTranslation();
  const [shared, setShared] = React.useState(false);

  const handleShare = async () => {
    const shareData = {
      title: 'Bake Rush',
      text: message,
      url: `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}ref=share`
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        GameAnalytics.trackShare(source, 'native');
      } else {
        await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
        setShared(true);
        setTimeout(() => setShared(false), 2000);
        GameAnalytics.trackShare(source, 'clipboard');
      }
    } catch (err) {
      console.warn('Error sharing:', err);
      GameAnalytics.trackShare(source, 'error');
    }
  };

  return (
    <Button 
      onClick={handleShare}
      variant="ghost"
      className={`inline-flex items-center gap-1.5 text-stone-400 hover:text-stone-600 transition-colors text-sm h-auto ${className}`}
      >
      <Share2 size={16} />
      {shared ? t('game.copied') : t('game.share')}
    </Button>
  );
};

// Home Button Component
const HomeButton = ({ className, onClick }) => {
  const { t } = useTranslation();

  return (
    <Button
      onClick={onClick}
      variant="ghost"
      className={`inline-flex items-center gap-1.5 text-stone-400 hover:text-stone-600 transition-colors text-sm h-auto ${className}`}
    >
      <Home size={16} />
      {t('game.home')}
    </Button>
  );
};

// Training Mode Button Component
const TrainingButton = ({ className, onClick, text }) => {
  const { t } = useTranslation();

  return (
    <Button
      onClick={onClick}
      variant="ghost"
      className={`inline-flex items-center gap-1.5 text-stone-400 hover:text-stone-600 transition-colors text-sm h-auto ${className}`}
    >
      <Umbrella size={16} />
      {text || t('game.training')}
    </Button>
  );
};


const CancelButton = ({ onClick, className }) => (
  <button
    onClick={onClick}
    className={`text-stone-400 hover:text-stone-600 transition-colors ${className}`}
    aria-label="Cancel game"
  >
    <X size={16} />
  </button>
);


export { Button, buttonVariants, ShareButton, HomeButton, TrainingButton, CancelButton }